import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Tv, Info, Loader2, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { 
  getCablePlans, 
  verifyCableCard, 
  buyCable 
} from "../../api/apiServices";

// Cable smartcard validation matching metadata configuration parameters
const AUTO_VERIFY_LENGTHS = [10, 11];
const AUTO_VERIFY_DEBOUNCE_MS = 600;
const BENEFICIARY_STORAGE_KEY = "fintech:cable:beneficiaries";
const MAX_SAVED_BENEFICIARIES = 5;

function loadBeneficiaries() {
  try {
    const raw = localStorage.getItem(BENEFICIARY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistBeneficiary(entry) {
  try {
    const existing = loadBeneficiaries().filter(
      (b) => !(b.smartcardNo === entry.smartcardNo && b.provider === entry.provider)
    );
    const next = [entry, ...existing].slice(0, MAX_SAVED_BENEFICIARIES);
    localStorage.setItem(BENEFICIARY_STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadBeneficiaries();
  }
}

export default function CablePayment() {
  const navigate = useNavigate();

  // --- UI & Stepper States ---
  const [step, setStep] = useState(1); // 1: Form, 2: PIN, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // --- API Data States ---
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [bouquets, setBouquets] = useState([]);

  // --- Form Inputs ---
  const [selectedCable, setSelectedCable] = useState("dstv"); 
  const [selectedBouquet, setSelectedBouquet] = useState(null);
  const [smartCardNumber, setSmartCardNumber] = useState("");
  const [transactionPin, setTransactionPin] = useState("");

  // --- Verification Response ---
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [verifiedKey, setVerifiedKey] = useState(null); // tracking snapshot: "provider:card"
  const [verifyingCard, setVerifyingCard] = useState(false);

  // --- Beneficiaries Array Storage ---
  const [beneficiaries, setBeneficiaries] = useState([]);

  const standardProviders = [
    { id: "dstv", name: "DSTV" },
    { id: "gotv", name: "GOtv" },
    { id: "startimes", name: "Startimes" }
  ];

  // Initialize cached local parameters safely
  useEffect(() => {
    setBeneficiaries(loadBeneficiaries());
  }, []);

  // Fetch bouquets dynamically when provider changes
  useEffect(() => {
    const fetchPlans = async () => {
      if (!selectedCable) {
        setBouquets([]);
        return;
      }
      try {
        setLoadingProviders(true);
        setError("");
        setSelectedBouquet(null); // Reset choice on provider switch
        const response = await getCablePlans(selectedCable);
        
        if (response && response.plans && Array.isArray(response.plans)) {
          setBouquets(response.plans);
        } else if (Array.isArray(response)) {
          setBouquets(response);
        } else {
          setBouquets([]);
          setError(`No dynamic packages returned for ${selectedCable.toUpperCase()}.`);
        }
      } catch (err) {
        console.error("Cable plans query execution failed", err);
        setError(err?.response?.data?.message || `Could not load ${selectedCable.toUpperCase()} packages.`);
        setBouquets([]);
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchPlans();
  }, [selectedCable]);

  // Handle Smart Card Verification (Memoized validation routine)
  const handleVerifyCard = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!selectedCable || !smartCardNumber) {
      setError("Please select a provider and input your card/IUC number.");
      return;
    }

    setError("");
    setVerifyingCard(true);
    setVerifiedCustomer(null);

    try {
      const payload = { 
        provider: selectedCable, 
        smartcardNo: smartCardNumber 
      };
      const res = await verifyCableCard(payload);

      if (res && res.status) {
        setVerifiedCustomer(res);
        setVerifiedKey(`${selectedCable}:${smartCardNumber}`);
      } else {
        setError(res?.message || "Smart card verification failed.");
      }
    } catch (err) {
      console.error("Cable verification error", err);
      setError(err?.response?.data?.message || "Verification error. Please try again.");
    } finally {
      setVerifyingCard(false);
    }
  }, [selectedCable, smartCardNumber]);

  // Automated layout hooks capturing string lengths configurations
  useEffect(() => {
    if (!selectedCable || !smartCardNumber) return;
    if (!AUTO_VERIFY_LENGTHS.includes(smartCardNumber.length)) return;

    const currentKey = `${selectedCable}:${smartCardNumber}`;
    if (currentKey === verifiedKey) return;
    if (verifyingCard) return;

    const handle = setTimeout(() => {
      handleVerifyCard();
    }, AUTO_VERIFY_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [smartCardNumber, selectedCable, verifiedKey, verifyingCard, handleVerifyCard]);

  const handleUseBeneficiary = (b) => {
    setSelectedCable(b.provider || "dstv");
    setSmartCardNumber(b.smartcardNo || "");
    
    const simulatedVerification = {
      status: true,
      customerName: b.customerName
    };
    setVerifiedCustomer(simulatedVerification);
    setVerifiedKey(`${b.provider}:${b.smartcardNo}`);
    setError("");
  };

  const handleProceedToPay = () => {
    if (!smartCardNumber) {
      setError("Please enter your Smart Card / IUC number.");
      return;
    }
    if (!verifiedCustomer) {
      setError("Please click 'Verify' to validate your customer information before making payment.");
      return;
    }
    if (!selectedBouquet) {
      setError("Please choose a package bouquet configuration.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleFinalPayment = async (e) => {
    e.preventDefault();
    if (transactionPin.length < 4) {
      setError("Input your 4-digit Transaction PIN.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const payload = {
        provider: selectedCable,
        smartcardNo: smartCardNumber,
        variationCode: selectedBouquet.variationCode,
        amount: Number(selectedBouquet.amount),
        pin: transactionPin
      };

      await buyCable(payload);

      // Cache current layout properties dynamically as beneficiary configurations 
      const labelName = standardProviders.find(p => p.id === selectedCable)?.name || selectedCable;
      const entry = {
        id: `${selectedCable}:${smartCardNumber}`,
        provider: selectedCable,
        providerLabel: labelName,
        smartcardNo: smartCardNumber,
        customerName: verifiedCustomer?.customerName || "Cable User",
        timestamp: Date.now()
      };
      setBeneficiaries(persistBeneficiary(entry));

      setStep(3);
    } catch (err) {
      console.error("Cable payment error", err);
      setError(err?.response?.data?.message || "Transaction failed. Check funds or transaction PIN.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="bg-white border-b px-4 py-3.5 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <button
          onClick={() => (step === 1 ? navigate(-1) : setStep(step - 1))}
          className="p-1 hover:bg-gray-100 rounded-full transition"
        >
          <ArrowLeft size={22} className="text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-gray-800">Cable TV Payment</h1>
      </header>

      <main className="px-4 pt-6 max-w-md mx-auto">
        {error && (
          <div className="mb-4 bg-red-50 text-red-600 border border-red-200 text-sm rounded-xl p-4 font-medium flex items-center gap-2">
            <Info size={18} />
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Saved Decoder Cards Deck Container */}
              {beneficiaries.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Star size={12} className="text-amber-500 fill-amber-500" /> Saved Smartcards
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-none">
                    {beneficiaries.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => handleUseBeneficiary(b)}
                        className="flex-shrink-0 flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-left hover:border-[#3866A3] transition shadow-sm"
                      >
                        <div className="w-7 h-7 bg-blue-50 text-[#3866A3] rounded-full flex items-center justify-center text-xs font-black shrink-0">
                          {b.providerLabel.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0 max-w-[120px]">
                          <p className="text-xs font-bold text-gray-800 truncate">
                            {b.customerName}
                          </p>
                          <p className="text-[10px] text-gray-400 font-mono truncate">{b.smartcardNo}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Card 1: Account Setup */}
              <div className="bg-white rounded-2xl border p-5 shadow-sm space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Select Cable TV Operator
                  </label>
                  <select
                    value={selectedCable}
                    onChange={(e) => {
                      setSelectedCable(e.target.value);
                      setVerifiedCustomer(null);
                      setVerifiedKey(null);
                    }}
                    className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition uppercase font-semibold"
                  >
                    {standardProviders.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Smart Card / IUC Number
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Enter SmartCard/IUC"
                      value={smartCardNumber}
                      onChange={(e) => {
                        setSmartCardNumber(e.target.value);
                        setVerifiedCustomer(null);
                      }}
                      className="flex-1 h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition"
                    />
                    <button
                      type="button"
                      onClick={(e) => handleVerifyCard(e)}
                      disabled={verifyingCard || !smartCardNumber || !selectedCable}
                      className="bg-[#3866A3] text-white px-4 rounded-xl text-sm font-semibold hover:bg-[#2e558a] transition disabled:opacity-50 flex items-center justify-center min-w-[90px]"
                    >
                      {verifyingCard ? <Loader2 size={18} className="animate-spin" /> : "Verify"}
                    </button>
                  </div>
                </div>

                {verifiedCustomer && (
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3"
                  >
                    <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} />
                    <div className="text-xs">
                      <p className="font-bold text-emerald-800">Verified Customer details</p>
                      <p className="font-medium text-gray-700 mt-1 uppercase">
                        Account Name: {verifiedCustomer.customerName || "Verified Account"}
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Card 2: Plan Selection */}
              <div className="bg-white rounded-2xl border p-5 shadow-sm space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Select Package / Bouquet
                  </label>
                  <p className="text-xs text-gray-400 mb-3">Choose one plan layout selection options below</p>
                  
                  {loadingProviders ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((n) => (
                        <div key={n} className="h-16 bg-gray-50 animate-pulse border border-gray-100 rounded-xl" />
                      ))}
                    </div>
                  ) : bouquets.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-400">
                      No plans found for this network operator.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                      {bouquets.map((b) => {
                        const isSelected = selectedBouquet?.variationCode === b.variationCode;
                        return (
                          <div
                            key={b.variationCode}
                            onClick={() => setSelectedBouquet(b)}
                            className={`p-4 rounded-xl border-2 transition cursor-pointer flex items-center justify-between ${
                              isSelected 
                                ? "border-[#3866A3] bg-blue-50/40 shadow-sm" 
                                : "border-gray-150 bg-white hover:border-gray-300"
                            }`}
                          >
                            <div className="flex-1 min-w-0 pr-2">
                              <p className={`text-sm font-bold truncate ${isSelected ? "text-[#3866A3]" : "text-gray-800"}`}>
                                {b.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5">
                                {b.variationCode}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-extrabold text-gray-900">
                                ₦{Number(b.amount).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button
                  onClick={handleProceedToPay}
                  disabled={loadingProviders || !selectedBouquet}
                  className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition shadow-md mt-2 disabled:opacity-50"
                >
                  Proceed to pay ₦{selectedBouquet ? Number(selectedBouquet.amount).toLocaleString() : "0"}
                </button>
              </div>
            </motion.div>
          )}

          {/* STEP 2: PIN Confirmation screen */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl border p-6 shadow-md space-y-6 text-center"
            >
              <div className="w-14 h-14 bg-[#E9EDF6] rounded-full flex items-center justify-center mx-auto text-[#3866A3]">
                <Tv size={28} />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-800">Confirm Subscription</h3>
                <p className="text-gray-500 text-sm mt-1">Please confirm details below before pay</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-500">Service</span>
                  <span className="font-semibold text-gray-800 uppercase">{selectedCable}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Bouquet</span>
                  <span className="font-semibold text-gray-800">{selectedBouquet?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">IUC/SmartCard</span>
                  <span className="font-semibold text-gray-800">{smartCardNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Account Name</span>
                  <span className="font-semibold text-gray-800 uppercase">{verifiedCustomer?.customerName}</span>
                </div>
                <hr className="border-gray-200 my-1" />
                <div className="flex justify-between text-base font-bold">
                  <span className="text-gray-700">Total Amount</span>
                  <span className="text-[#3866A3]">₦{Number(selectedBouquet?.amount).toLocaleString()}</span>
                </div>
              </div>

              <form onSubmit={handleFinalPayment} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Enter Transaction PIN
                  </label>
                  <input
                    type="password"
                    maxLength={4}
                    value={transactionPin}
                    onChange={(e) => setTransactionPin(e.target.value)}
                    placeholder="••••"
                    className="w-32 h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-center text-xl font-bold tracking-widest focus:outline-none focus:border-[#3866A3] transition mx-auto block"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition disabled:opacity-75 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Processing...
                    </>
                  ) : (
                    `Pay ₦${Number(selectedBouquet?.amount).toLocaleString()}`
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* STEP 3: Success Screen */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl border p-6 shadow-md text-center space-y-6"
            >
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto text-emerald-500">
                <CheckCircle2 size={40} />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-800">Subscription Successful!</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Your Cable TV bouquet will be reactivated momentarily.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-500">Bouquet Package</span>
                  <span className="font-bold text-gray-800">{selectedBouquet?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Card Number</span>
                  <span className="font-medium text-gray-800">{smartCardNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction Status</span>
                  <span className="text-emerald-600 font-bold">COMPLETED</span>
                </div>
              </div>

              <button
                onClick={() => navigate("/")}
                className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition"
              >
                Back to Dashboard
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

// 2009597883